import { CREDIT_API_URL } from '../constants';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `${token}`
  };
};

const handleUnauthorized = async (response) => {
  if (response.status === 401 || response.status === 403) {
    try {
      const errorData = await response.json();
      if (errorData.message && (errorData.message.includes('expired') || errorData.message.includes('invalid'))) {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('userInfo');
        window.location.href = '/';
        throw new Error('Session expired. Please login again.');
      }
    } catch (e) {
      // If parsing fails, still handle unauthorized
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('userInfo');
      window.location.href = '/';
      throw new Error('Session expired. Please login again.');
    }
  }
};

export const adminAnchorService = {
  // Get list of anchors with filters
  getAnchors: async (filters = {}) => {
    const {
      startDate,
      endDate,
      companyName,
      gstNumber,
      status,
      contactName,
      contactMobile,
      page = 1,
      limit = 10
    } = filters;

    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    if (companyName) queryParams.append('companyName', companyName);
    if (gstNumber) queryParams.append('gstNumber', gstNumber);
    if (status) queryParams.append('verificationStatus', status);
    if (contactName) queryParams.append('contactName', contactName);
    if (contactMobile) queryParams.append('contactMobile', contactMobile);
    queryParams.append('page', page);
    queryParams.append('limit', limit);

    const response = await fetch(
      `${CREDIT_API_URL}anchor/list?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    await handleUnauthorized(response);

    if (!response.ok) {
      // Check if response is JSON or HTML
      const contentType = response.headers.get('content-type');
      let errorMessage = 'Failed to fetch anchors';
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If JSON parsing fails, use default message
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
      } else {
        // Handle HTML responses (like 404 pages)
        if (response.status === 404) {
          errorMessage = 'Anchor list endpoint not found. Please contact support.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Request failed: ${response.status} ${response.statusText}`;
        }
      }
      
      throw new Error(errorMessage);
    }

    // Check content-type before parsing JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format. Expected JSON but received ' + (contentType || 'unknown format'));
    }

    try {
      return await response.json();
    } catch (e) {
      throw new Error('Failed to parse server response. Please try again.');
    }
  },

  // Get anchor details by ID
  getAnchorDetails: async (anchorId) => {
    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    await handleUnauthorized(response);

    if (!response.ok) {
      // Check if response is JSON or HTML
      const contentType = response.headers.get('content-type');
      let errorMessage = 'Failed to fetch anchor details';
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
      } else {
        if (response.status === 404) {
          errorMessage = 'Anchor details not found.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Request failed: ${response.status} ${response.statusText}`;
        }
      }
      
      throw new Error(errorMessage);
    }

    // Check content-type before parsing JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format. Expected JSON but received ' + (contentType || 'unknown format'));
    }

    try {
      return await response.json();
    } catch (e) {
      throw new Error('Failed to parse server response. Please try again.');
    }
  },

  // Update anchor (unified API for status and/or remarks)
  // Supports: PATCH /credit/anchor/:anchorId
  // Body: { verificationStatus?: 'Verified' | 'Rejected' | 'Pending', remarks?: string }
  updateAnchor: async (anchorId, updates) => {
    // Validate that at least one field is provided
    if (!updates || (!updates.verificationStatus && !updates.remarks)) {
      throw new Error('At least one field (verificationStatus or remarks) must be provided');
    }

    const response = await fetch(
      `${CREDIT_API_URL}anchor/${anchorId}`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      }
    );

    await handleUnauthorized(response);

    if (!response.ok) {
      // Check if response is JSON or HTML
      const contentType = response.headers.get('content-type');
      let errorMessage = 'Failed to update anchor';
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
      } else {
        if (response.status === 404) {
          errorMessage = 'Anchor not found.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Request failed: ${response.status} ${response.statusText}`;
        }
      }
      
      throw new Error(errorMessage);
    }

    // Check content-type before parsing JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format. Expected JSON but received ' + (contentType || 'unknown format'));
    }

    try {
      return await response.json();
    } catch (e) {
      throw new Error('Failed to parse server response. Please try again.');
    }
  },

  // Update anchor status (Approve/Reject) - Uses new unified API
  updateAnchorStatus: async (anchorId, verificationStatus, remarks = '') => {
    const updates = { verificationStatus };
    if (remarks) {
      updates.remarks = remarks;
    }
    return adminAnchorService.updateAnchor(anchorId, updates);
  },

  // Approve anchor - Uses new unified API
  approveAnchor: async (anchorId, remarks = '') => {
    const updates = { verificationStatus: 'Verified' };
    if (remarks) {
      updates.remarks = remarks;
    } else {
      updates.remarks = 'Documents verified successfully';
    }
    return adminAnchorService.updateAnchor(anchorId, updates);
  },

  // Reject anchor - Uses new unified API
  rejectAnchor: async (anchorId, remarks) => {
    if (!remarks || !remarks.trim()) {
      throw new Error('Rejection reason is required');
    }
    return adminAnchorService.updateAnchor(anchorId, {
      verificationStatus: 'Rejected',
      remarks: remarks.trim()
    });
  },

  // Update anchor remarks/notes only - Uses new unified API
  updateAnchorRemarks: async (anchorId, data) => {
    return adminAnchorService.updateAnchor(anchorId, {
      remarks: data.remarks
    });
  }
};
